import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axios/axiosInstance';
import { featuresForCompanyContext } from 'constants/companyFeatures';

const EMPTY_INVITE = { email: '', companyName: '' };
const EMPTY_ADD = { legalName: '', email: '', phone: '' };

const ResellerCompanies = () => {
    const [companies, setCompanies] = useState([]);
    const [firm, setFirm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [tab, setTab] = useState('list');
    const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
    const [addForm, setAddForm] = useState(EMPTY_ADD);
    const [saving, setSaving] = useState(false);
    const [invites, setInvites] = useState([]);
    const [removingId, setRemovingId] = useState(null);
    const currentCompanyId = localStorage.getItem('companyId');

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [firmRes, coRes] = await Promise.all([
                axiosInstance.get('/firms/mine'),
                axiosInstance.get('/firms/mine/companies'),
            ]);
            setFirm(firmRes.data);
            setCompanies(Array.isArray(coRes.data) ? coRes.data : []);
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to load companies.', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchInvites = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/invites/list');
            const all = Array.isArray(res.data) ? res.data : [];
            setInvites(all.filter(i => i.inviteType === 'company'));
        } catch {
            setInvites([]);
        }
    }, []);

    useEffect(() => {
        refresh();
        fetchInvites();
    }, [refresh, fetchInvites]);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteForm.email.trim()) { showToast('Email is required.', 'error'); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/invites/send', {
                email: inviteForm.email.trim(),
                inviteType: 'company',
                companyName: inviteForm.companyName.trim() || null,
            });
            showToast(`Invite sent to ${inviteForm.email}`);
            setInviteForm(EMPTY_INVITE);
            setTab('list');
            fetchInvites();
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to send invite.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!addForm.legalName.trim()) { showToast('Company name is required.', 'error'); return; }
        setSaving(true);
        try {
            const res = await axiosInstance.post('/auth/add-company', {
                legalName: addForm.legalName.trim(),
                email: addForm.email.trim() || null,
                phone: addForm.phone.trim() || null,
            });
            showToast(`Company "${res.data.legalName || addForm.legalName}" added.`);
            setAddForm(EMPTY_ADD);
            setTab('list');
            await refresh();
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to add company.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleOpen = async (companyId) => {
        try {
            const res = await axiosInstance.post('/auth/switch-company', { companyId });
            localStorage.setItem('authToken', res.data.token);
            localStorage.setItem('companyId', companyId);
            localStorage.setItem('userRole', 'Reseller');
            localStorage.setItem(
                'userFeatures',
                JSON.stringify(featuresForCompanyContext('Reseller', res.data.features))
            );
            // Enter company dashboard while keeping Reseller ability to return via /reseller
            window.location.href = '/dashboard';
        } catch {
            showToast('Failed to open company.', 'error');
        }
    };

    const displayName = (c) => c.tradeName || c.legalName || c.id;

    const handleRemove = async (company) => {
        const name = displayName(company);
        const ok = window.confirm(
            `Remove "${name}" from your firm?\n\n` +
            'The company is not deleted. You will still be billed for the current month ' +
            '(pro-rated from the join date through month end).'
        );
        if (!ok) return;
        setRemovingId(company.id);
        try {
            await axiosInstance.delete(`/firms/mine/companies/${encodeURIComponent(company.id)}`);
            showToast(`Removed "${name}" from your firm.`);
            if (localStorage.getItem('companyId') === company.id) {
                localStorage.removeItem('companyId');
            }
            await refresh();
        } catch (err) {
            const msg = err?.response?.data?.detail
                || err?.response?.data?.message
                || err?.response?.data?.inner
                || 'Failed to remove company.';
            showToast(msg, 'error');
            // eslint-disable-next-line no-console
            console.error('Remove company failed', err?.response?.status, err?.response?.data);
        } finally {
            setRemovingId(null);
        }
    };

    const atLimit = firm && firm.maxCompanies >= 0 && companies.length >= firm.maxCompanies;

    return (
        <div>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>
                        {firm?.name || 'Client Companies'}
                    </h1>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>
                        {firm
                            ? `${companies.length} of ${firm.maxCompanies < 0 ? '∞' : firm.maxCompanies} companies`
                            : 'Manage client companies under your firm.'}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={() => setTab(tab === 'invite' ? 'list' : 'invite')}
                        disabled={atLimit && tab !== 'invite'}
                        style={{
                            background: tab === 'invite' ? '#fff' : 'var(--brand-primary, #E7730A)',
                            color: tab === 'invite' ? '#E7730A' : '#fff',
                            border: tab === 'invite' ? '1px solid #E7730A' : 'none',
                            borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600,
                            cursor: atLimit && tab !== 'invite' ? 'not-allowed' : 'pointer',
                            opacity: atLimit && tab !== 'invite' ? 0.5 : 1,
                        }}
                    >
                        {tab === 'invite' ? 'Cancel' : '✉ Invite Company'}
                    </button>
                    <button
                        onClick={() => setTab(tab === 'add' ? 'list' : 'add')}
                        disabled={atLimit && tab !== 'add'}
                        style={{
                            background: tab === 'add' ? '#fff' : '#1e293b',
                            color: tab === 'add' ? '#1e293b' : '#fff',
                            border: tab === 'add' ? '1px solid #1e293b' : 'none',
                            borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600,
                            cursor: atLimit && tab !== 'add' ? 'not-allowed' : 'pointer',
                            opacity: atLimit && tab !== 'add' ? 0.5 : 1,
                        }}
                    >
                        {tab === 'add' ? 'Cancel' : '+ Add Company'}
                    </button>
                </div>
            </div>

            {atLimit && (
                <div style={{
                    background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13,
                }}>
                    Company limit reached. Contact platform support to raise Max Companies.
                </div>
            )}

            {tab === 'invite' && (
                <form onSubmit={handleInvite} style={{
                    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                    padding: 24, marginBottom: 24, maxWidth: 520,
                }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Invite a Client Company</h3>
                    <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                        They receive an email to register. Once complete, the company is linked to your firm.
                    </p>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Company Admin Email *</label>
                    <input
                        type="email" required value={inviteForm.email}
                        onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="admin@clientcompany.com"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Company Name (optional)</label>
                    <input
                        type="text" value={inviteForm.companyName}
                        onChange={e => setInviteForm(f => ({ ...f, companyName: e.target.value }))}
                        placeholder="Acme Corp"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 16, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <button type="submit" disabled={saving} style={{
                        background: 'var(--brand-primary, #E7730A)', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
                    }}>
                        {saving ? 'Sending…' : 'Send Invite'}
                    </button>
                </form>
            )}

            {tab === 'add' && (
                <form onSubmit={handleAdd} style={{
                    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                    padding: 24, marginBottom: 24, maxWidth: 520,
                }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Add Company Directly</h3>
                    <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                        Creates the company under your firm and opens it for payroll setup.
                    </p>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Legal Name *</label>
                    <input
                        type="text" required value={addForm.legalName}
                        onChange={e => setAddForm(f => ({ ...f, legalName: e.target.value }))}
                        placeholder="Acme Corporation"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Company Email</label>
                    <input
                        type="email" value={addForm.email}
                        onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="info@acme.com"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Phone</label>
                    <input
                        type="text" value={addForm.phone}
                        onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 16, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <button type="submit" disabled={saving} style={{
                        background: '#1e293b', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
                    }}>
                        {saving ? 'Creating…' : 'Create Company'}
                    </button>
                </form>
            )}

            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 24 }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                    Companies ({companies.length})
                </div>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
                ) : companies.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>🏢</div>
                        <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>No companies yet</div>
                        <div style={{ fontSize: 13, color: '#94a3b8' }}>Invite a client or add a company to get started.</div>
                    </div>
                ) : (
                    companies.map(c => {
                        const isCurrent = c.id === currentCompanyId;
                        return (
                            <div key={c.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '14px 18px', borderBottom: '1px solid #f8fafc',
                                background: isCurrent ? '#fff8f3' : '#fff',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 10,
                                        background: isCurrent ? 'linear-gradient(135deg, #E7730A, #f0a05a)' : '#f1f5f9',
                                        color: isCurrent ? '#fff' : '#64748b',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: 14,
                                    }}>
                                        {displayName(c).charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{displayName(c)}</div>
                                        {c.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.email}</div>}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(c)}
                                        disabled={removingId === c.id}
                                        style={{
                                            background: '#fff', border: '1px solid #fca5a5', color: '#dc2626',
                                            borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                                            cursor: removingId === c.id ? 'wait' : 'pointer',
                                        }}
                                    >
                                        {removingId === c.id ? 'Removing…' : 'Remove'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleOpen(c.id)}
                                        style={{
                                            background: 'var(--brand-primary, #E7730A)', border: 'none', color: '#fff',
                                            borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                        }}
                                    >
                                        Open
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {invites.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                        Company Invites Sent
                    </div>
                    {invites.map(inv => (
                        <div key={inv.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '12px 18px', borderBottom: '1px solid #f8fafc', fontSize: 13,
                        }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>{inv.email}</div>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                    Sent {new Date(inv.createdAt).toLocaleDateString()}
                                </div>
                            </div>
                            <span style={{
                                fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                                background: inv.status === 'pending' ? '#fef3c7' : '#f3f4f6',
                                color: inv.status === 'pending' ? '#b45309' : '#6b7280',
                            }}>
                                {inv.status}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ResellerCompanies;
